/**
 * Utility to restore Matt's daily habits
 *
 * Run this from browser console while logged in as admin:
 * 1. Open the app in Chrome
 * 2. Open Developer Tools (Cmd+Option+I)
 * 3. Go to Console tab
 * 4. Import and run:
 *
 *    import('/src/utils/restoreMattHabits.js').then(m => m.restoreMattHabits())
 *
 *    Or for dry-run (preview only):
 *    import('/src/utils/restoreMattHabits.js').then(m => m.restoreMattHabits(true))
 */

import { collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';
import { db, auth } from './firebase';

// Matt's details
const MATT = {
  name: 'Matt',
  uid: 'XMTwVzM3yqWsJsEC0xR6JmfkeS82',
  email: 'matt.garvey@entrusted.com'
};

export async function restoreMattHabits(dryRun = false) {
  console.log('========================================');
  console.log("  Restore Matt's Habits");
  console.log('========================================');
  console.log(`\nMatt's UID: ${MATT.uid}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN (no changes will be made)' : 'LIVE (changes will be applied)'}\n`);

  // Get current user
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('❌ Not logged in! Please log in first.');
    return { error: 'Not logged in' };
  }
  console.log(`Logged in as: ${currentUser.email} (${currentUser.uid})\n`);

  // Query only links owned by current user (respects Firestore security rules)
  const q = query(collection(db, 'links'), where('userId', '==', currentUser.uid));
  const linksSnapshot = await getDocs(q);

  let totalLinks = 0;
  let linksWithMattLegacy = 0;
  let linksWithMattCurrent = 0;
  let linksMissingMatt = 0;
  let linksFixed = 0;

  const habitsToFix = [];
  const mattCurrentHabits = [];

  console.log('=== Scanning All Habits/Links ===\n');

  for (const docSnap of linksSnapshot.docs) {
    const data = docSnap.data();
    totalLinks++;

    const legacyAssignments = data.legacyAssignments || {};
    const currentAssignments = data.assignments || {};

    const mattInLegacy = 'Matt' in legacyAssignments;
    const mattInCurrent = MATT.uid in currentAssignments;

    if (mattInLegacy) {
      linksWithMattLegacy++;
    }

    if (mattInCurrent) {
      linksWithMattCurrent++;
      mattCurrentHabits.push({
        id: docSnap.id,
        name: data.name,
        frequency: currentAssignments[MATT.uid]
      });
    }

    // Check if Matt is in legacy but missing from current
    if (mattInLegacy && !mattInCurrent) {
      linksMissingMatt++;
      const legacyValue = legacyAssignments['Matt'];
      habitsToFix.push({
        id: docSnap.id,
        name: data.name,
        url: data.url,
        legacyFrequency: typeof legacyValue === 'string' ? legacyValue : legacyValue?.frequency || legacyValue,
        currentAssignments: currentAssignments
      });
    }
  }

  console.log('\n=== Summary ===\n');
  console.log(`Total habits/links: ${totalLinks}`);
  console.log(`Habits with Matt in legacyAssignments: ${linksWithMattLegacy}`);
  console.log(`Habits with Matt in current assignments: ${linksWithMattCurrent}`);
  console.log(`Habits missing Matt (needs fix): ${linksMissingMatt}`);

  if (mattCurrentHabits.length > 0) {
    console.log("\n=== Matt's Current Habits ===\n");
    for (const habit of mattCurrentHabits) {
      const freq = typeof habit.frequency === 'string' ? habit.frequency : habit.frequency?.frequency || JSON.stringify(habit.frequency);
      console.log(`✅ ${habit.name} - ${freq}`);
    }
  }

  if (habitsToFix.length > 0) {
    console.log('\n=== Habits to Restore for Matt ===\n');

    for (const habit of habitsToFix) {
      console.log(`🔧 ${habit.name}`);
      console.log(`   ID: ${habit.id}`);
      console.log(`   Legacy frequency: ${JSON.stringify(habit.legacyFrequency)}`);

      if (!dryRun) {
        const newAssignments = { ...habit.currentAssignments };
        newAssignments[MATT.uid] = habit.legacyFrequency;

        await updateDoc(doc(db, 'links', habit.id), {
          assignments: newAssignments
        });

        linksFixed++;
        console.log(`   ✅ FIXED - Added Matt with frequency: ${JSON.stringify(habit.legacyFrequency)}`);
      } else {
        console.log(`   Would add Matt with frequency: ${JSON.stringify(habit.legacyFrequency)}`);
      }
      console.log('');
    }

    if (dryRun) {
      console.log(`\n⚠️  DRY RUN - No changes made. Run restoreMattHabits(false) to apply fixes.`);
    } else {
      console.log(`\n✅ Fixed ${linksFixed} habits for Matt.`);
    }

    return { fixed: linksFixed, toFix: habitsToFix.length, mattCurrentHabits, habitsToFix };
  } else {
    console.log("\n✅ No habits need fixing - Matt's assignments are all in sync!");

    if (mattCurrentHabits.length === 0) {
      console.log('\n⚠️  Note: Matt has no habits assigned at all.');
      console.log('   This could mean:');
      console.log('   1. Matt was never assigned any habits originally');
      console.log('   2. The legacyAssignments were cleared or never created');
      console.log('   3. The habits were deleted');
      console.log('\n   You may need to manually assign habits to Matt through the UI.');
    }

    return { fixed: 0, toFix: 0, mattCurrentHabits, habitsToFix: [] };
  }
}

// Also export a function to list all habits for debugging
export async function listAllHabits() {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('❌ Not logged in! Please log in first.');
    return [];
  }

  const q = query(collection(db, 'links'), where('userId', '==', currentUser.uid));
  const linksSnapshot = await getDocs(q);

  const habits = [];
  for (const docSnap of linksSnapshot.docs) {
    const data = docSnap.data();
    habits.push({
      id: docSnap.id,
      name: data.name,
      url: data.url,
      isQuickLink: data.isQuickLink,
      pinned: data.pinned,
      archived: data.archived,
      assignments: data.assignments || {},
      legacyAssignments: data.legacyAssignments || {}
    });
  }

  console.table(habits.map(h => ({
    name: h.name,
    assignments: JSON.stringify(h.assignments),
    legacy: JSON.stringify(h.legacyAssignments)
  })));

  return habits;
}

// Transfer ownership of Matt's habits to Matt
export async function transferHabitsToMatt(dryRun = false) {
  console.log('========================================');
  console.log("  Transfer Habits to Matt");
  console.log('========================================');
  console.log(`\nMatt's UID: ${MATT.uid}`);
  console.log(`Mode: ${dryRun ? 'DRY RUN (no changes will be made)' : 'LIVE (changes will be applied)'}\n`);

  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('❌ Not logged in! Please log in first.');
    return { error: 'Not logged in' };
  }
  console.log(`Logged in as: ${currentUser.email} (${currentUser.uid})\n`);

  // Query links owned by current user
  const q = query(collection(db, 'links'), where('userId', '==', currentUser.uid));
  const linksSnapshot = await getDocs(q);

  const habitsToTransfer = [];
  let transferred = 0;

  for (const docSnap of linksSnapshot.docs) {
    const data = docSnap.data();
    const assignments = data.assignments || {};

    // Check if Matt is assigned to this habit
    if (MATT.uid in assignments) {
      habitsToTransfer.push({
        id: docSnap.id,
        name: data.name,
        frequency: assignments[MATT.uid]
      });
    }
  }

  console.log(`Found ${habitsToTransfer.length} habits assigned to Matt\n`);

  if (habitsToTransfer.length === 0) {
    console.log('No habits to transfer.');
    return { transferred: 0, habits: [] };
  }

  console.log('=== Habits to Transfer ===\n');

  for (const habit of habitsToTransfer) {
    console.log(`📋 ${habit.name} (${habit.frequency})`);

    if (!dryRun) {
      await updateDoc(doc(db, 'links', habit.id), {
        userId: MATT.uid
      });
      transferred++;
      console.log(`   ✅ Transferred to Matt`);
    } else {
      console.log(`   Would transfer to Matt`);
    }
  }

  if (dryRun) {
    console.log(`\n⚠️  DRY RUN - No changes made. Run transferHabitsToMatt(false) to apply.`);
  } else {
    console.log(`\n✅ Transferred ${transferred} habits to Matt.`);
    console.log(`\nMatt can now see these habits when he logs in!`);
  }

  return { transferred, habits: habitsToTransfer };
}

// Make functions available globally for console access
if (typeof window !== 'undefined') {
  window.restoreMattHabits = restoreMattHabits;
  window.listAllHabits = listAllHabits;
  window.transferHabitsToMatt = transferHabitsToMatt;
}
